/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { getCart, cartItemCount } from "@/lib/storeCart";

// Minimal, generic public-storefront chrome — reused across every /store/:slug/* page.
// Not a site-specific design (each real client site keeps its own hand-built public
// pages); this is the standard commerce layer any commerce-enabled website gets.
export function useStoreParams() {
  const { slug } = useParams();
  return { slug };
}

export default function StoreShell({ slug, businessName, children }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const refresh = () => setCount(cartItemCount(getCart(slug)));
    refresh();
    window.addEventListener("storage", refresh);
    window.addEventListener("mysitecare:store-cart-changed", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("mysitecare:store-cart-changed", refresh);
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#E2E8F0] bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <Link to={`/store/${slug}`} className="font-bold font-display text-[#0F172A]">{businessName || "Shop"}</Link>
          <Link to={`/store/${slug}/cart`} className="relative inline-flex items-center gap-1.5 text-sm font-medium text-[#0F172A]" data-testid="store-cart-link">
            <ShoppingBag size={18} />
            {count > 0 && <span className="ml-0.5 rounded-full bg-[#0F172A] px-1.5 py-0.5 text-xs font-bold text-white">{count}</span>}
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-8">{children}</main>
    </div>
  );
}
